/**
 * Module-level bridge between non-React handlers and a Fluent UI ``Toaster``.
 *
 * Mirrors the pill-bridge pattern: the handler map in ``handlers.ts`` lives
 * outside the React tree, so it can't call ``useToastController`` directly.
 * A React component registers a sink once on mount; handlers fire
 * ``emitToast`` and the sink translates it into a ``dispatchToast`` call.
 *
 * Currently used by:
 *  - ``clipboard.copy`` to confirm a successful clipboard write.
 */

export type ToastIntent = "success" | "error" | "info";

export interface ToastPayload {
  /** Short, user-facing title (no em dashes, plain language). */
  readonly title: string;
  /** Visual intent — drives icon + colour. */
  readonly intent: ToastIntent;
}

type ToastSink = (toast: ToastPayload) => void;

let sink: ToastSink | null = null;

/** React component calls this on mount; closure typically calls ``dispatchToast``. */
export function registerToastSink(next: ToastSink): () => void {
  sink = next;
  return () => {
    if (sink === next) sink = null;
  };
}

/** Handlers call this. No-op if no React tree has registered a sink yet
 *  (e.g. unit tests, or the toast fires before the chat panel mounts). */
export function emitToast(toast: ToastPayload): void {
  sink?.(toast);
}
